import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  CircleHelp,
  Code2,
  Eye,
  FileText,
  Send,
  ThumbsDown,
  ThumbsUp,
  type LucideIcon,
} from "lucide-react";
import {
  HELP_RUN_PHASE_LABELS,
  isHelpLinkPath,
  type HelpSourceKind,
} from "@ttc-spectree/toolbox-contract";
import { MarkdownRenderer } from "@/components/common/markdown-renderer";
import type { HelpAnswerMessage, HelpAttachment, HelpPersonMessage } from "@/hooks/use-help-bridge";
import { helpDisplayText } from "@/lib/toolbox/help";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";
import {
  HelpConfirmAction,
  shownHelpActions,
  type HelpActionHandlers,
} from "./help-confirm-action";

/**
 * One message of a Help conversation (epic PI-SPEC-SW-E302, feature
 * PI-SPEC-SW-1557, frames H2, H3 and H6).
 *
 * - The person's bubble, with the screenshots they sent.
 * - Help's answer: markdown (never raw HTML; links only to allowlisted
 *   in-app paths), a blue caret and the activity phrase while it streams,
 *   in-app links, confirm buttons (only actions on the fixed list with a
 *   page handler), sources, and "Did this help?" (local only).
 * - Verdicts (H6): `off-topic` is the single redirect line; `not-sure` offers
 *   Send to Aaron inline; the rest are answers.
 */

const SOURCE_ICONS: Readonly<Record<HelpSourceKind, LucideIcon>> = {
  guide: BookOpen,
  screen: Eye,
  code: Code2,
  doc: FileText,
};

const smallButton =
  "inline-flex h-[26px] items-center gap-1.5 whitespace-nowrap rounded-lg border border-[#27272A] bg-[#1A1A1E] px-[9px] text-xs font-medium text-[#FAFAFA] transition-colors hover:bg-[#222226]";

/** A screenshot thumbnail (design `.shot`). */
export function HelpThumbnail({
  attachment,
  onRemove,
  size = "large",
}: {
  attachment: HelpAttachment;
  onRemove?: (() => void) | undefined;
  size?: "large" | "small";
}) {
  return (
    <div
      className={cn(
        "relative flex flex-none items-end overflow-hidden rounded-md border border-[#3F3F46] bg-[#141417] p-1.5",
        size === "large" ? "h-[82px] w-[132px]" : "h-16 w-[104px]"
      )}
      data-testid="help-thumbnail"
    >
      <img
        src={attachment.previewUrl}
        alt={attachment.image.name}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <span className="relative max-w-full truncate rounded-[3px] bg-[rgba(9,9,11,0.85)] px-1 font-mono text-[10px] text-[#D4D4D8]">
        {attachment.image.name}
      </span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${attachment.image.name}`}
          className="absolute right-1 top-1 flex h-[18px] w-[18px] items-center justify-center rounded-full border border-[#3F3F46] bg-[#09090B] text-[11px] leading-none text-[#D4D4D8] hover:text-white"
        >
          ×
        </button>
      )}
    </div>
  );
}

export function HelpPersonBubble({ message }: { message: HelpPersonMessage }) {
  return (
    <div
      className="flex max-w-[300px] flex-col gap-2 self-end rounded-[12px_12px_4px_12px] border border-[#27272A] bg-[#1A1A1E] px-3 py-[9px] text-[13px] leading-[1.5] text-[#FAFAFA]"
      data-testid="help-person-message"
    >
      {message.images.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {message.images.map((attachment) => (
            <HelpThumbnail key={attachment.id} attachment={attachment} />
          ))}
        </div>
      )}
      <span className="whitespace-pre-wrap break-words">{message.text}</span>
    </div>
  );
}

function HelpWho() {
  return (
    <span className="flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.02em] text-muted-foreground">
      <span className="flex h-4 w-4 items-center justify-center rounded-[5px] border border-[#27272A] bg-[#1A1A1E]">
        <CircleHelp className="h-[11px] w-[11px] text-[#D4D4D8]" aria-hidden="true" />
      </span>
      Help
    </span>
  );
}

interface HelpAnswerProps {
  message: HelpAnswerMessage;
  handlers: HelpActionHandlers;
  onSendToAaron?: ((answer: HelpAnswerMessage) => void) | undefined;
}

export function HelpAnswer({ message, handlers, onSendToAaron }: HelpAnswerProps) {
  const [feedback, setFeedback] = useState<"yes" | "no" | null>(null);
  const text = helpDisplayText(message.text, message.streaming);
  const waiting = message.streaming && text.length === 0;
  const activity =
    message.activity ??
    (message.phase ? HELP_RUN_PHASE_LABELS[message.phase] : HELP_RUN_PHASE_LABELS.starting);
  const finished = !message.streaming && message.outcome === "answered";
  const offTopic = finished && message.verdict === "off-topic";
  const notSure = finished && message.verdict === "not-sure";
  const actions = finished && !offTopic ? shownHelpActions(message.actions, handlers) : [];
  const links =
    finished && !offTopic ? message.links.filter((link) => isHelpLinkPath(link.path)) : [];

  return (
    <div
      className="flex flex-col gap-2 text-[13px] leading-[1.55] text-[#E4E4E7]"
      data-testid="help-answer"
      data-streaming={message.streaming ? "true" : "false"}
      data-verdict={message.verdict ?? ""}
      aria-busy={message.streaming}
    >
      <HelpWho />
      {message.streaming && (
        <span
          className="flex items-center gap-1.5 text-xs text-[#6CC4FF]"
          data-testid="help-activity"
        >
          <span
            className="h-2.5 w-2.5 flex-none animate-spin rounded-full border-2 border-[#23A4FF] border-r-transparent"
            aria-hidden="true"
          />
          {activity}…
        </span>
      )}
      {!waiting && text.length > 0 && (
        <div className={cn(message.streaming && "[&>div>*:last-child]:inline [&>div]:inline")}>
          <MarkdownRenderer
            content={text}
            allowLink={isHelpLinkPath}
            className="[&_ol]:list-outside [&_ol]:pl-[18px] [&_p]:mb-2 [&_ul]:list-outside [&_ul]:pl-[18px]"
          />
          {message.streaming && (
            <span
              className="ml-0.5 inline-block h-[14px] w-[7px] rounded-[1px] bg-[#6CC4FF] align-[-2px]"
              data-testid="help-caret"
              aria-hidden="true"
            />
          )}
        </div>
      )}
      {waiting && (
        <span
          className="inline-block h-[14px] w-[7px] rounded-[1px] bg-[#6CC4FF]"
          data-testid="help-caret"
          aria-hidden="true"
        />
      )}
      {message.outcome === "stopped" && (
        <span className={cn("text-xs", TOOLBOX_TEXT.faint)}>Stopped.</span>
      )}
      {message.outcome === "failed" && (
        <span role="alert" className="text-[13px] text-[#FBBF24]" data-testid="help-answer-error">
          {message.errorMessage ?? "Something went wrong. Ask again."}
        </span>
      )}
      {(links.length > 0 || actions.length > 0) && (
        <div className="flex flex-col gap-2">
          {links.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {links.map((link) => (
                <Link
                  key={`${link.path}-${link.label}`}
                  to={link.path}
                  className={smallButton}
                  data-testid="help-link"
                >
                  <ArrowRight className="h-[13px] w-[13px]" aria-hidden="true" />
                  {link.label}
                </Link>
              ))}
            </div>
          )}
          {actions.map((action) => {
            const handler = handlers[action.action];
            if (!handler) return null;
            return (
              <HelpConfirmAction
                key={`${action.action}-${JSON.stringify(action.args)}`}
                action={action}
                handler={handler}
              />
            );
          })}
        </div>
      )}
      {notSure && onSendToAaron && (
        <div className="flex gap-2">
          <button
            type="button"
            className={smallButton}
            onClick={() => {
              onSendToAaron(message);
            }}
            data-testid="help-send-to-aaron-inline"
          >
            <Send className="h-[13px] w-[13px]" aria-hidden="true" />
            Send to Aaron
          </button>
        </div>
      )}
      {finished && !offTopic && message.sources.length > 0 && (
        <div className="flex flex-wrap gap-1.5" aria-label="Sources" data-testid="help-sources">
          {message.sources.map((source, index) => {
            const Icon = SOURCE_ICONS[source.kind];
            return (
              <span
                key={`${source.kind}-${source.title}-${String(index)}`}
                className="inline-flex h-[22px] max-w-full items-center gap-[5px] rounded-md border border-[#1F1F23] bg-[#151518] px-2 text-[11px] text-muted-foreground"
              >
                <Icon className="h-3 w-3 flex-none" aria-hidden="true" />
                <span className="truncate">{source.title}</span>
              </span>
            );
          })}
        </div>
      )}
      {finished && !offTopic && !notSure && (
        <div
          className={cn("flex items-center gap-3 text-xs", TOOLBOX_TEXT.faint)}
          data-testid="help-feedback"
        >
          <span>Did this help?</span>
          <button
            type="button"
            aria-pressed={feedback === "yes"}
            onClick={() => {
              setFeedback(feedback === "yes" ? null : "yes");
            }}
            className={cn(
              "flex items-center gap-1 hover:text-[#FAFAFA]",
              feedback === "yes" && "text-[#FAFAFA]"
            )}
          >
            <ThumbsUp className="h-[13px] w-[13px]" aria-hidden="true" />
            Yes
          </button>
          <button
            type="button"
            aria-pressed={feedback === "no"}
            onClick={() => {
              setFeedback(feedback === "no" ? null : "no");
            }}
            className={cn(
              "flex items-center gap-1 hover:text-[#FAFAFA]",
              feedback === "no" && "text-[#FAFAFA]"
            )}
          >
            <ThumbsDown className="h-[13px] w-[13px]" aria-hidden="true" />
            No
          </button>
          <span className="flex-1" />
          {onSendToAaron && (
            <button
              type="button"
              onClick={() => {
                onSendToAaron(message);
              }}
              className="flex items-center gap-1 text-[#6CC4FF] hover:text-[#23A4FF]"
              data-testid="help-send-to-aaron"
            >
              <Send className="h-[13px] w-[13px]" aria-hidden="true" />
              Send to Aaron
            </button>
          )}
        </div>
      )}
    </div>
  );
}
